import type { QueryClient } from '@tanstack/react-query'
import { invalidateQuoteOverview } from '../api/queries'
import type { Quote } from '../api/types'
import { assistantText } from './conversation'
import type { AssistantWorker } from './types'

/** A worker's short visible name, never the private task text behind it. */
export function workerLabel(worker: AssistantWorker) {
  const clean = assistantText(worker.label || worker.question).split(/[\n,;:.!?]/)[0]?.replace(/\s+/g, ' ').trim()
  if (!clean) return 'Quote work'
  if (!worker.label) {
    // Only recover the subject of a recognizable instruction, never its private tail.
    const lead = clean.match(
      /^(?:please\s+)?(?:find|select|choose|source|add|recommend|identify|research|configure|compare|check|locate|pick)(?:\s+and\s+(?:add|configure|select|recommend))?\s+(?:(?:a|an|the|suitable|appropriate|matching)\s+)*/i,
    )
    if (!lead) return 'Quote work'
    const subject = clean.slice(lead[0].length).split(/\s+(?:for|to|then|with|using|based on|including|that|which)\b/i)[0]?.trim()
    if (!subject || subject.length > 60 || !/^[a-z0-9 /&'()-]+$/i.test(subject)) return 'Quote work'
    return subject.toLowerCase()
  }
  return clean.length > 60 ? `${clean.slice(0, 59).trimEnd()}…` : clean
}

export function workLabel(value: string | undefined, complete = false): string | null {
  // Saved steps start with "Checked", which assistantText otherwise treats as diagnostics.
  const text = assistantText(complete ? value?.replace(/^Checked\b/i, 'Checking') : value).replace(/\s+/g, ' ').trim()
  if (!text) return null
  // An import names its file, and its agent turn what it proposed for each item (USA-1340, USA-1342).
  if (/^(?:Suggested|Kept|Marked|Importing|Imported) /.test(text)) return text
  if (/^queued$/i.test(text)) return 'Queued'
  if (/^retrying\b/i.test(text)) return 'Retrying'
  if (/^resuming\b/i.test(text)) return 'Resuming saved work'
  if (/^(?:selecting|configuring)\b/i.test(text)) return complete ? 'Configured equipment' : 'Configuring equipment'
  if (/\b(?:prices?|pricing)\b/i.test(text) && /\b(?:read|check|review)\w*\b/i.test(text)) return complete ? 'Checked prices' : 'Checking prices'
  if (/\b(?:catalog|search|products?|configuration|compare)\b/i.test(text) && /\b(?:search|find|look|check|read)\w*\b/i.test(text)) {
    return complete ? 'Searched catalog' : 'Searching catalog'
  }
  if (/\bshipping\b/i.test(text) && /\b(?:read|check|review)\w*\b/i.test(text)) return complete ? 'Checked shipping' : 'Checking shipping'
  if (/\b(?:customer|salespeople)\b/i.test(text) && /\b(?:read|check|review)\w*\b/i.test(text)) {
    return complete ? 'Checked customer details' : 'Checking customer details'
  }
  if (/\b(?:quotes?|workflow)\b/i.test(text) && /\b(?:read|check|review)\w*\b/i.test(text)) return complete ? 'Read quote' : 'Reading quote'
  if (/^(?:create|update|add|remove|swap|set|configure|publish|send|build)\b/i.test(text)) {
    return complete ? 'Prepared quote changes' : 'Preparing quote changes'
  }
  return complete ? null : 'Working on quote'
}

export function workHistory(steps: string[] | undefined, activity?: string[]): string[] {
  const history = [
    ...(activity || []).map((step) => workLabel(step)),
    ...(steps || []).map((step) => workLabel(step, true)),
  ].filter((label): label is string => !!label)
  return [...new Set(history.filter((label) => label !== 'Working on quote' || history.length === 1))]
}

/** Put a quote the assistant saved into every cached read, never stepping back a revision. */
export function refreshAssistantQuote(client: QueryClient, next: Quote) {
  const current = client.getQueryData<Quote>(['quote', next.id])
  if (current && current.revision > next.revision) return
  // Cancel first: a read started before the applied event must not replace its quote.
  void client.cancelQueries({ queryKey: ['quote', next.id], exact: true })
  client.setQueryData(['quote', next.id], next)
  client.setQueryData<Quote[]>(['quotes'], (quotes) => (quotes ? [next, ...quotes.filter((item) => item.id !== next.id)] : quotes))
  for (const key of [
    'quote-activity',
    'quote-versions',
    'quote-workflow',
    'quote-shipping',
    'quote-services',
    'quote-pricing',
    'quote-taxes',
    'quote-financing',
    'quote-invoicing',
    'quote-deliveries',
    'purchase-orders',
  ]) {
    void client.cancelQueries({ queryKey: [key, next.id] })
    void client.invalidateQueries({ queryKey: [key, next.id] })
  }
  void invalidateQuoteOverview(client)
  for (const key of ['customers', 'customer-search', 'customer']) void client.invalidateQueries({ queryKey: [key] })
}
